/**
 * ResponseList Component
 * Displays a list of form responses with expand/collapse details
 */

import * as React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Text,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Divider,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  EyeRegular,
  DeleteRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import type { FormResponse, FieldDefinition, FieldType } from '@structured-request/core';

// Resolves a blobName to a SAS URL and renders an <img> or loading text
const BlobImage: React.FC<{ blobName: string; fileName?: string; style?: React.CSSProperties }> = ({ blobName, fileName, style }) => {
  const [url, setUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`/api/media-sas?blobName=${encodeURIComponent(blobName)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled) { if (data?.url) setUrl(data.url); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [blobName]);
  if (loading) return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Loading image...</Text>;
  if (!url) return <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{fileName ?? 'Image unavailable'}</Text>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Click to open full image">
      <img src={url} alt={fileName ?? 'image'} style={{ ...style, cursor: 'pointer' }} />
    </a>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: tokens.spacingVerticalS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  secureValue: {
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusSmall,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

export interface ResponseListProps {
  /** List of responses to display */
  responses: FormResponse[];
  /** Field definitions for display labels */
  fields: FieldDefinition[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when response is deleted */
  onDelete?: (responseId: string) => void;
  /** Callback when export is requested */
  onExport?: () => void;
  /** Show the card wrapper (default: true) */
  showCard?: boolean;
  /** Title for the card header */
  title?: string;
  /** Render additional action buttons for each row */
  rowActions?: (response: FormResponse) => React.ReactNode;
}

export const ResponseList: React.FC<ResponseListProps> = ({
  responses,
  fields,
  isLoading = false,
  onDelete,
  onExport,
  showCard = true,
  title = 'Responses',
  rowActions,
}) => {
  const styles = useStyles();
  const [_selectedResponse, setSelectedResponse] = React.useState<FormResponse | null>(null);

  const getFieldLabel = (fieldName: string): string => {
    const field = fields.find((f) => f.name === fieldName);
    return field?.label ?? fieldName;
  };

  const getFieldType = (fieldName: string): FieldType | undefined => {
    return fields.find((f) => f.name === fieldName)?.type;
  };

  const isImageDataUrl = (v: unknown): v is string =>
    typeof v === 'string' && v.startsWith('data:image');

  const isPhotoObject = (v: unknown): v is { dataUrl?: string; fileName?: string; blobName?: string } =>
    typeof v === 'object' && v !== null && ('dataUrl' in v || 'fileName' in v || 'blobName' in v);

  const isBlobRef = (v: unknown): v is { blobName: string; fileName?: string } =>
    typeof v === 'object' && v !== null && 'blobName' in v && typeof (v as any).blobName === 'string';


  const formatValue = (value: unknown, fieldName?: string): string => {
    if (value === null || value === undefined) return '-';
    if (isImageDataUrl(value)) return '[image]';
    if (isBlobRef(value)) return '[image]';
    if (isPhotoObject(value)) return '[image]';
    if (Array.isArray(value)) {
      // Repeater rows — show count
      if (value.length > 0 && typeof value[0] === 'object') return `${value.length} row(s)`;
      return value.join(', ');
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    const type = fieldName ? getFieldType(fieldName) : undefined;
    if ((type === 'photo' || type === 'signature') && typeof value === 'string' && value.length > 100) return '[image]';
    return String(value);
  };

  const imgStyle: React.CSSProperties = {
    maxWidth: '100%', maxHeight: '200px', objectFit: 'contain',
    borderRadius: tokens.borderRadiusSmall, border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'block',
  };

  // Render a field value — images get an <img> tag, repeaters get a count badge, everything else is text
  const renderDetailValue = (fieldName: string, value: unknown) => {
    const type = getFieldType(fieldName);

    // Blob reference — fetch SAS URL on demand via BlobImage component
    if (isBlobRef(value)) {
      return <BlobImage blobName={value.blobName} fileName={value.fileName} style={imgStyle} />;
    }

    // Photo object with dataUrl (legacy / local dev)
    if (isPhotoObject(value) && value.dataUrl) {
      return <img src={value.dataUrl} alt={value.fileName ?? 'photo'} style={imgStyle} />;
    }

    // Bare dataUrl string (photo/signature stored as string)
    if (isImageDataUrl(value)) {
      return <img src={value} alt={type ?? 'image'} style={imgStyle} />;
    }

    // Repeater rows — summary list
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      const subFields = fields.find((f) => f.name === fieldName)?.subFields ?? [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
          {(value as Record<string, unknown>[]).map((row, i) => {
            const summary = subFields
              .slice(0, 3)
              .map((sf) => row[sf.name])
              .filter((v) => v !== undefined && v !== null && v !== '')
              .join(' · ');
            return <Text key={i} size={200}>{i + 1}. {summary || '(empty row)'}</Text>;
          })}
        </div>
      );
    }

    return <Text>{formatValue(value, fieldName)}</Text>;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  const content = (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.emptyState}>
          <Spinner label="Loading responses..." />
        </div>
      ) : responses.length === 0 ? (
        <div className={styles.emptyState}>
          <Text>No responses yet.</Text>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Submitted</TableHeaderCell>
              <TableHeaderCell>Respondent</TableHeaderCell>
              <TableHeaderCell>Preview</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {responses.map((response) => {
              // Preview: first 2 non-image, non-repeater values
              const previewFields = Object.entries(response.data)
                .filter(([key, val]) => {
                  const t = getFieldType(key);
                  if (t === 'photo' || t === 'signature') return false;
                  if (isImageDataUrl(val) || isPhotoObject(val)) return false;
                  if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') return false;
                  return true;
                })
                .slice(0, 2);
              const preview = previewFields
                .map(([key, val]) => `${getFieldLabel(key)}: ${formatValue(val, key)}`)
                .join(', ');

              return (
                <TableRow key={response.id}>
                  <TableCell>
                    <TableCellLayout>
                      {formatDate(response.submittedAt)}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      {response.respondentEmail || response.respondentName || 'Anonymous'}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      <Text
                        size={200}
                        style={{
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {preview || '-'}
                      </Text>
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actions}>
                      <Dialog>
                        <DialogTrigger disableButtonEnhancement>
                          <Button
                            icon={<EyeRegular />}
                            appearance="subtle"
                            size="small"
                            onClick={() => setSelectedResponse(response)}
                          >
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogSurface>
                          <DialogBody>
                            <DialogTitle>Response Details</DialogTitle>
                            <DialogContent>
                              <div className={styles.detailsGrid}>
                                <Text className={styles.label}>Submitted:</Text>
                                <Text>{formatDate(response.submittedAt)}</Text>

                                {response.respondentEmail && (
                                  <>
                                    <Text className={styles.label}>Email:</Text>
                                    <Text>{response.respondentEmail}</Text>
                                  </>
                                )}

                                {response.respondentName && (
                                  <>
                                    <Text className={styles.label}>Name:</Text>
                                    <Text>{response.respondentName}</Text>
                                  </>
                                )}

                                <Divider style={{ gridColumn: '1 / -1', margin: `${tokens.spacingVerticalM} 0` }} />

                                {Object.entries(response.data).map(([key, value]) => (
                                  <React.Fragment key={key}>
                                    <Text className={styles.label}>{getFieldLabel(key)}:</Text>
                                    {renderDetailValue(key, value)}
                                  </React.Fragment>
                                ))}

                                {response.secureData && Object.keys(response.secureData).length > 0 && (
                                  <>
                                    <Divider style={{ gridColumn: '1 / -1', margin: `${tokens.spacingVerticalM} 0` }} />
                                    <Text className={styles.label} style={{ gridColumn: '1 / -1' }}>
                                      Secure Fields:
                                    </Text>
                                    {Object.entries(response.secureData).map(([key, _value]) => (
                                      <React.Fragment key={key}>
                                        <Text className={styles.label}>{getFieldLabel(key)}:</Text>
                                        <Text className={styles.secureValue}>••••••••</Text>
                                      </React.Fragment>
                                    ))}
                                  </>
                                )}
                              </div>
                            </DialogContent>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Close</Button>
                              </DialogTrigger>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>

                      {rowActions && rowActions(response)}

                      {onDelete && (
                        <Button
                          icon={<DeleteRegular />}
                          appearance="subtle"
                          size="small"
                          onClick={() => onDelete(response.id)}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardHeader
        header={<Text size={400} weight="semibold">{title}</Text>}
        description={<Text size={200}>{responses.length} response(s)</Text>}
        action={
          onExport && responses.length > 0 ? (
            <Button icon={<ArrowDownloadRegular />} appearance="subtle" onClick={onExport}>
              Export
            </Button>
          ) : undefined
        }
      />
      <Divider />
      <CardPreview style={{ padding: tokens.spacingVerticalL }}>
        {content}
      </CardPreview>
    </Card>
  );
};

export default ResponseList;
